import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Grid3X3, TrendingUp, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  candidats?: Array<{
    id: string;
    nom: string;
    score: number;
    decision: string;
    created_at: string;
    scores_criteres?: { [key: string]: number };
  }>;
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface HeatmapComparisonProps {
  rankings: SocieteRanking[];
  selectedCompanies: string[];
  selectedCriteria: string[];
}

interface CellDetails {
  company: SocieteRanking;
  criteriaKey: string;
  criteriaLabel: string;
  value: number;
}

export const HeatmapComparison: React.FC<HeatmapComparisonProps> = ({
  rankings,
  selectedCompanies,
  selectedCriteria
}) => {
  const [selectedCell, setSelectedCell] = useState<CellDetails | null>(null);

  if (selectedCompanies.length === 0 || selectedCriteria.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5" />
            Matrice de Chaleur Comparative
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Sélectionnez des sociétés et des critères pour afficher la matrice
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedRankings = rankings.filter(r => selectedCompanies.includes(r.societe_id));

  const getCriteriaLabel = (criteriaKey: string): string => {
    if (criteriaKey === 'overall_score') return 'Score Global';
    if (criteriaKey.startsWith('comp_')) return criteriaKey.replace('comp_', '');
    if (criteriaKey.startsWith('cert_')) return criteriaKey.replace('cert_', '');
    if (criteriaKey.startsWith('criteria_')) return criteriaKey.replace('criteria_', '');
    return criteriaKey;
  };

  const getValue = (ranking: SocieteRanking, criteriaKey: string): number => {
    if (criteriaKey === 'overall_score') {
      return ranking.score_moyen;
    } else if (criteriaKey.startsWith('criteria_')) {
      const criteriaName = criteriaKey.replace('criteria_', '');
      return ranking.scores_criteres[criteriaName]?.score || 0;
    }
    return 0;
  };

  const getColorIntensity = (value: number): string => {
    if (value >= 80) return 'bg-green-500';
    if (value >= 60) return 'bg-green-400';
    if (value >= 40) return 'bg-yellow-400';
    if (value >= 20) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getScoreLevel = (value: number): { label: string; color: string } => {
    if (value >= 80) return { label: 'Excellent', color: 'text-green-600' };
    if (value >= 60) return { label: 'Bon', color: 'text-green-500' };
    if (value >= 40) return { label: 'Moyen', color: 'text-yellow-600' };
    if (value >= 20) return { label: 'Faible', color: 'text-orange-500' };
    return { label: 'Très faible', color: 'text-red-500' };
  };

  const handleCellClick = (ranking: SocieteRanking, criteriaKey: string) => {
    const value = getValue(ranking, criteriaKey);
    setSelectedCell({
      company: ranking,
      criteriaKey,
      criteriaLabel: getCriteriaLabel(criteriaKey),
      value
    });
  };

  const getRankForCriteria = (ranking: SocieteRanking, criteriaKey: string): number => {
    const values = selectedRankings.map(r => ({
      id: r.societe_id,
      value: getValue(r, criteriaKey)
    })).sort((a, b) => b.value - a.value);
    
    return values.findIndex(v => v.id === ranking.societe_id) + 1;
  };

  const allValues = selectedRankings.flatMap(ranking =>
    selectedCriteria.map(criteria => getValue(ranking, criteria))
  );
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5" />
            Matrice de Chaleur Comparative
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Legend */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Intensité des scores (plus foncé = meilleur score)
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span>Faible</span>
                <div className="flex gap-1">
                  <div className="w-4 h-4 bg-red-400 rounded"></div>
                  <div className="w-4 h-4 bg-orange-400 rounded"></div>
                  <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                  <div className="w-4 h-4 bg-green-400 rounded"></div>
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                </div>
                <span>Élevé</span>
              </div>
            </div>

            {/* Heatmap Grid */}
            <div className="overflow-x-auto">
              <div className="min-w-max">
                {/* Header */}
                <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: `200px repeat(${selectedCriteria.length}, 120px)` }}>
                  <div className="font-medium text-sm"></div>
                  {selectedCriteria.map(criteria => (
                    <div key={criteria} className="font-medium text-xs text-center p-2 bg-muted rounded">
                      {getCriteriaLabel(criteria)}
                    </div>
                  ))}
                </div>

                {/* Data Rows */}
                {selectedRankings.map(ranking => (
                  <div key={ranking.societe_id} className="grid gap-1 mb-1" style={{ gridTemplateColumns: `200px repeat(${selectedCriteria.length}, 120px)` }}>
                    <div className="font-medium text-sm p-2 bg-muted rounded flex items-center">
                      {ranking.societe_nom}
                    </div>
                    {selectedCriteria.map(criteria => {
                      const value = getValue(ranking, criteria);
                      const colorClass = getColorIntensity(value);
                      return (
                        <div
                          key={criteria}
                          onClick={() => handleCellClick(ranking, criteria)}
                          className={`${colorClass} text-white text-center p-2 rounded font-medium text-sm flex flex-col items-center justify-center relative group cursor-pointer hover:ring-2 hover:ring-primary hover:ring-offset-2 transition-all`}
                        >
                          <span>{value.toFixed(1)}%</span>
                          <span className="text-xs opacity-80">({(value / 20).toFixed(2)}/5)</span>
                          
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            Cliquez pour plus de détails
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{maxValue.toFixed(1)}%</div>
                <div className="text-xs text-muted-foreground">Score max</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {(allValues.reduce((a, b) => a + b, 0) / allValues.length).toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Moyenne</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{selectedRankings.length}</div>
                <div className="text-xs text-muted-foreground">Sociétés</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{selectedCriteria.length}</div>
                <div className="text-xs text-muted-foreground">Critères</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={!!selectedCell} onOpenChange={() => setSelectedCell(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Détails du score
            </DialogTitle>
          </DialogHeader>
          
          {selectedCell && (
            <div className="space-y-4">
              {/* Company Info */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <Building2 className="h-8 w-8 text-muted-foreground" />
                <div>
                  <div className="font-semibold">{selectedCell.company.societe_nom}</div>
                  <div className="text-sm text-muted-foreground">
                    {selectedCell.company.nb_candidatures} candidat(s) évalué(s)
                  </div>
                </div>
              </div>

              {/* Criteria Score */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Critère</div>
                <div className="p-3 border rounded-lg">
                  <div className="font-medium">{selectedCell.criteriaLabel}</div>
                </div>
              </div>

              {/* Score Display */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <div className={`text-3xl font-bold ${getScoreLevel(selectedCell.value).color}`}>
                    {selectedCell.value.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Score</div>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <div className="text-3xl font-bold text-primary">
                    {(selectedCell.value / 20).toFixed(2)}/5
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Note</div>
                </div>
              </div>

              {/* Score Level */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm text-muted-foreground">Niveau</span>
                <Badge variant="outline" className={getScoreLevel(selectedCell.value).color}>
                  {getScoreLevel(selectedCell.value).label}
                </Badge>
              </div>

              {/* Rank */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm text-muted-foreground">Classement pour ce critère</span>
                <Badge variant="secondary">
                  {getRankForCriteria(selectedCell.company, selectedCell.criteriaKey)}/{selectedRankings.length}
                </Badge>
              </div>

              {/* Candidates if available */}
              {selectedCell.company.candidats && selectedCell.company.candidats.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Candidats évalués - Score pour "{selectedCell.criteriaLabel}"
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {selectedCell.company.candidats.slice(0, 5).map(candidat => {
                      // Get the score for the selected criterion, or use global score if not available
                      const criteriaName = selectedCell.criteriaKey === 'overall_score' 
                        ? null 
                        : selectedCell.criteriaKey.replace('criteria_', '');
                      const criteriaScore = criteriaName && candidat.scores_criteres 
                        ? candidat.scores_criteres[criteriaName] 
                        : undefined;
                      const displayScore = criteriaScore !== undefined ? criteriaScore : candidat.score;
                      const isGlobalScore = criteriaScore === undefined;
                      
                      return (
                        <div key={candidat.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                          <span className="truncate flex-1 mr-2">{candidat.nom}</span>
                          <Badge 
                            variant={
                              candidat.decision === 'shortlist' ? 'default' :
                              candidat.decision === 'consider' ? 'secondary' : 'outline'
                            }
                            className="text-xs shrink-0"
                          >
                            {isGlobalScore ? 'Global: ' : ''}{displayScore.toFixed(0)}%
                          </Badge>
                        </div>
                      );
                    })}
                    {selectedCell.company.candidats.length > 5 && (
                      <div className="text-xs text-muted-foreground text-center py-1">
                        +{selectedCell.company.candidats.length - 5} autres candidats
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};